import { WorkflowConversationAction } from '../HomeChat/WorkflowConversationPresentation';
import ChatBubbleDisclosure from '../ChatBubbleDisclosure';
import { TextField } from '../FarChat/ChatFeedbackOverlays';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { plainJustificationText } from '../../utils/plainJustificationText';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import AssistantMarkdown from '../FarChat/AssistantMarkdown';
import HomeChatWorkflowBanner from '../HomeChat/HomeChatWorkflowBanner';
import MarketResearchNotifyControls from '../MarketResearchNotifyControls';
import MarketResearchNotifyShareDialog from '../MarketResearchNotifyShareDialog';
import { isGenericStageFormCopy, withoutActiveStagePrompt } from '../HomeChat/stageFormGuidance';
import { MarketResearchMrrDetailsHandle, MarketResearchMrrDetailsPanel } from '../MarketResearchMrrDetailsPanel';
import MarketResearchEarlyPlan from '../MarketResearchEarlyPlan';
import { MarketResearchHandoffStatus } from '../MarketResearchHandoff';
import { MarketResearchOfficialActionStatus } from '../MarketResearchOfficialActions';
import { asMarketResearchHandoff } from '../../api/stageFunctions';
import { fetchWithChatFeedback } from '../../api/chatFeedbackRequired';
import VendorSelectionStaleWarning from '../VendorSelectionStaleWarning';
import MrChatLineItemsField from './MrChatLineItemsField';
import ProductProgramEvidence from '../../stages/ProductProgramEvidence';
import type { ProductSourceResults } from '../../api/stageFunctions';
import type { MarketResearchNotificationRequestResult } from '../../api/workflowNotifications';

import type {
  MrChatAnswerValue,
  MrChatCompletedStage,
  MrChatAnswerMap,
  MrChatField,
  MrChatFieldType,
  MrChatLineItem,
  MrChatMessage,
  MrChatPendingInput,
  MrChatSessionView,
  MrChatTurnInput,
  MrChatWorkflowHandoff,
} from './types';

function FlightChiefHandoffHint({ flowId }: { flowId: string }) {
  const [suggestion, setSuggestion] = useState<{ name: string; email: string } | null>(null);
  useEffect(() => {
    let active = true;
    fetchWithChatFeedback(`${process.env.REACT_APP_API_URL || ''}/api/flows/${flowId}/market-research/flight-chief-suggestion`, { credentials: 'include' })
      .then(async response => response.ok ? response.json() : null)
      .then(data => {
        const value = data?.suggestion;
        if (active && typeof value?.name === 'string' && typeof value?.email === 'string') setSuggestion(value);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [flowId]);
  return suggestion ? <Typography variant="body2" sx={{ mt: 1 }}>
    Suggested flight chief: {suggestion.name} ({suggestion.email}). Open the workflow to share it with them.
  </Typography> : null;
}

function requestId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `mr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function display(value: unknown, fallback = ''): string {
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : fallback;
}

function initiatingRequestText(view: MrChatSessionView | null): string {
  const request = view?.initiating_request;
  if (typeof request === 'string') return request.trim();
  if (request && typeof request === 'object') return display(request.content).trim();
  return '';
}

function fieldKey(field: MrChatField): string {
  return typeof field.key === 'string' && field.key ? field.key
    : typeof field.id === 'string' && field.id ? field.id
      : '';
}

function fieldLabel(field: MrChatField): string {
  return display(field.question ?? field.label, fieldKey(field) || 'Context question');
}

/** Mirrors the normal MR question rule: explicit required flags win, then priority. */
function fieldIsRequired(field: MrChatField): boolean {
  if (typeof field.required === 'boolean') return field.required;
  if (typeof field.isRequired === 'boolean') return field.isRequired;
  return field.priority === 'High' || field.priority === 1 || field.priority === 2;
}

/** Field-level completion notices are already communicated beside the inputs. */
function isInlineValidationError(
  error: NonNullable<MrChatPendingInput['validation_errors']>[number],
  fields: MrChatField[],
  values: MrChatAnswerMap,
): boolean {
  const code = display(error.code);
  const path = display(error.field_path);
  const message = display(error.message);
  const schedule = fields.find((field) => fieldInputType(field) === 'line_items');
  const scheduleRows = schedule && values[fieldKey(schedule)];
  const rows = isStructuredAnswerArray(scheduleRows) ? scheduleRows : [];
  if (code === 'line_items_required' && schedule && fieldIsRequired(schedule)) return true;
  const missingColumn = /^(?:line_items|core\.schedule)\[(\d+)\]\.(quantity|uom|description)$/.exec(path);
  // Older saved chat contracts may contain the message without structured metadata.
  const legacyMissingColumn = /^Line (\S+) is missing (quantity|uom|description)\.$/.exec(message);
  const columnKey = missingColumn?.[2] || legacyMissingColumn?.[2];
  if (columnKey && schedule && rows.length && (schedule.item_fields || []).some((column) =>
    fieldKey(column) === columnKey && (column.required === true || column.isRequired === true))) {
    if (missingColumn) return Number(missingColumn[1]) < rows.length;
    return rows.some((row, index) => display(row.clin_number, String(index + 1)) === legacyMissingColumn?.[1]);
  }
  if (path && /^(Complete|Provide|Select|Acknowledge)\b/i.test(message)
    && fields.some((field) => fieldIsRequired(field)
      && !['boolean', 'file', 'files', 'line_items'].includes(fieldInputType(field))
      && (fieldKey(field) === path || fieldKey(field).endsWith(`.${path}`)))) return true;
  return false;
}

function initialValues(pending: MrChatPendingInput | null | undefined): MrChatAnswerMap {
  return Object.fromEntries((pending?.fields || []).flatMap((field) => {
    const key = fieldKey(field);
    const value = field.value ?? field.default ?? field.candidate;
    return key && (typeof value === 'string' || typeof value === 'number'
      || typeof value === 'boolean' || (Array.isArray(value)
        && (value.every((item) => typeof item === 'string')
          || isStructuredAnswerArray(value))))
      ? [[key, value]]
      : [];
  }));
}

/**
 * The controller owns form shape.  Values, however, belong to the browser
 * until a turn is accepted.  A progress refresh for this exact contract must
 * therefore not be treated as a new form.
 */
function pendingFormIdentity(view: MrChatSessionView | null | undefined): string {
  const pending = view?.pending_input;
  const stage = display(view?.current_stage?.key) || display(pending?.stage_key);
  const fields = (pending?.fields || []).map((field) => `${fieldKey(field)}:${fieldLabel(field)}`).join('|');
  return [display(view?.session_id), stage, display(pending?.kind), fields].join('::');
}

function isStringArray(value: MrChatAnswerValue | undefined): value is string[] {
  return Array.isArray(value)
    && (value as unknown[]).every((item): item is string => typeof item === 'string');
}

function isStructuredAnswerArray(value: unknown): value is MrChatLineItem[] {
  return Array.isArray(value) && value.every((item) => Boolean(item)
    && typeof item === 'object' && !Array.isArray(item));
}

function isMissingAnswer(value: unknown): boolean {
  return value === undefined || value === null
    || (typeof value === 'string' && value.trim() === '')
    || (Array.isArray(value) && value.length === 0);
}

function fieldInputType(field: MrChatField): MrChatFieldType {
  const type = field.input_type ?? field.field_type ?? field.type;
  if (type === 'form_type' || type === 'form-type' || type === 'form_type_choice') return 'form_type_choice';
  if (type === 'checkbox_group' || type === 'multi_select') return 'multi_select';
  if (type === 'checkboxes') return 'checkboxes';
  if (type === 'line_items') return 'line_items';
  return type === 'text' || type === 'textarea' || type === 'number' || type === 'date' || type === 'email'
    || type === 'url' || type === 'select' || type === 'radio' || type === 'boolean' || type === 'file' || type === 'files'
    ? type
    : 'text';
}

function missingRequiredLineItemCells(
  fields: MrChatField[],
  values: MrChatAnswerMap,
): string[] {
  return fields.flatMap((field) => {
    if (fieldInputType(field) !== 'line_items') return [];
    const value = values[fieldKey(field)];
    if (!isStructuredAnswerArray(value)) return [];
    const requiredColumns = (field.item_fields || []).filter(
      (column) => Boolean(fieldKey(column)) && fieldIsRequired(column),
    );
    return value.flatMap((row, rowIndex) => requiredColumns.flatMap((column) => {
      const key = fieldKey(column) as keyof MrChatLineItem;
      return isMissingAnswer(row[key])
        ? [`${fieldLabel(field)}, line ${rowIndex + 1}: ${fieldLabel(column)}`]
        : [];
    }));
  });
}

function synopsisFormDescription(value: string): string {
  if (value === 'combined') return 'Use the combined synopsis and solicitation for a streamlined commercial purchase.';
  if (value === 'sf18') return 'Use SF 18 when the request is issued on the standard request-for-quotations form.';
  if (value === 'sf1449') return 'Use SF 1449 for commercial products or services requiring that solicitation form.';
  return '';
}

function options(field: MrChatField): Array<{ value: string; label: string; description: string }> {
  return (field.options || field.choices || []).flatMap((option) => typeof option === 'string'
    ? [{ value: option, label: option, description: synopsisFormDescription(option) }]
    : option && (typeof option.value === 'string' || typeof option.id === 'string')
      ? [{
        value: String(option.value ?? option.id),
        label: display(option.label ?? option.name, String(option.value ?? option.id)),
        description: display(option.description ?? option.help_text) || synopsisFormDescription(String(option.value ?? option.id)),
      }]
      : []);
}

function synopsisFormTypeField(pending: MrChatPendingInput | null): MrChatField | null {
  const choices = pending?.form_type_choices || pending?.form_type_options || pending?.form_types || [];
  return choices.length ? {
    key: 'form_type', label: 'Synopsis form type', input_type: 'form_type_choice',
    options: choices, required: pending?.form_type_required !== false,
  } : null;
}

function isSynopsisFormSelector(field: MrChatField): boolean {
  const key = fieldKey(field);
  return key === 'instrument_type' || key === 'form_type';
}

function initialFieldValue(field: MrChatField): MrChatAnswerValue | undefined {
  const value = field.value ?? field.default ?? field.candidate;
  return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    || (Array.isArray(value) && (value.every((item) => typeof item === 'string')
      || isStructuredAnswerArray(value)))
    ? value
    : undefined;
}

function synopsisFieldsForType(pending: MrChatPendingInput | null, formType: string): MrChatField[] {
  const byType = pending?.form_fields_by_type;
  if (byType && Array.isArray(byType[formType])) return byType[formType];
  // Legacy controller views contain only the current selected-form fields.
  return (pending?.fields || []).filter((field) => !isSynopsisFormSelector(field)
    && fieldKey(field) !== 'issuance_attestation');
}

function synopsisInteractiveFields(pending: MrChatPendingInput | null, formType: string): MrChatField[] {
  const issued = pending?.fields || [];
  const selector = issued.find(isSynopsisFormSelector) || synopsisFormTypeField(pending);
  const attestation = issued.filter((field) => fieldKey(field) === 'issuance_attestation');
  return [selector, ...synopsisFieldsForType(pending, formType), ...attestation]
    .filter((field): field is MrChatField => Boolean(field))
    .filter((field) => field.visible !== false && Boolean(fieldKey(field)));
}

function valuesForFields(values: MrChatAnswerMap, fields: MrChatField[]): MrChatAnswerMap {
  return Object.fromEntries(fields.flatMap((field) => {
    const key = fieldKey(field);
    const value = values[key];
    return key && !isMissingAnswer(value) ? [[key, value]] : [];
  }));
}

function completedStageKey(stage: MrChatCompletedStage): string {
  return stage.stage_key || display(stage.label, 'completed-stage');
}

/** A turn may carry only newly completed stages; completed history never regresses. */
function mergeCompletedStages(previous: MrChatCompletedStage[] | undefined, incoming: MrChatCompletedStage[] | undefined): MrChatCompletedStage[] {
  const merged = new Map<string, MrChatCompletedStage>();
  (previous || []).forEach((stage) => merged.set(completedStageKey(stage), stage));
  (incoming || []).forEach((stage) => merged.set(completedStageKey(stage), stage));
  return Array.from(merged.values());
}

/** Rehydrate projections may contain only their durable entry message. Keep
 * the already-rendered transcript until the server explicitly advances it. */
function mergeMessages(previous: MrChatMessage[] | undefined, incoming: MrChatMessage[] | undefined): MrChatMessage[] {
  const merged = [...(previous || [])];
  (incoming || []).forEach((message) => {
    const exists = merged.some((existing) => existing.role === message.role
      && display(existing.content) === display(message.content));
    if (!exists) merged.push(message);
  });
  return merged;
}

function readOnlyResultText(value: unknown): string {
  const format = (item: unknown, depth = 0): string => {
    if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') return String(item);
    if (Array.isArray(item)) return item.slice(0, 20).map((entry) => format(entry, depth + 1)).filter(Boolean).join('\n');
    if (item && typeof item === 'object' && depth < 5) {
      return Object.entries(item as Record<string, unknown>).slice(0, 30)
        .map(([key, entry]) => `${key.replace(/_/g, ' ')}: ${/(api[_-]?key|authorization|token|password|secret)/i.test(key)
          ? '[redacted]'
          : format(entry, depth + 1)}`).join('\n');
    }
    return '';
  };
  // This is a bounded, plain-text view of server-issued workflow data. React
  // escapes it by default; the bound also keeps a large hydrated record from
  // overwhelming the active conversation.
  return format(value).slice(0, 12000);
}

function stageResultText(stage: MrChatCompletedStage): string {
  // `context` may be the controller's aggregate input from earlier stages;
  // a completed card is a review of this stage only.
  return [stage.data, stage.output, stage.result, stage.results, stage.preview]
    .map(readOnlyResultText)
    .find(Boolean) || '';
}

function stageResultData(stage: MrChatCompletedStage): unknown {
  return [stage.data, stage.output, stage.result, stage.results, stage.preview]
    .find((candidate) => Boolean(readOnlyResultText(candidate)));
}

type DetailRecord = Record<string, unknown>;

function isRecord(value: unknown): value is DetailRecord {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isSensitiveDetailKey(key: string): boolean {
  return /(api[_-]?key|authorization|token|password|secret|file_path)/i.test(key);
}

function scalarText(value: unknown, fallback = '—'): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map((item) => scalarText(item, '')).filter(Boolean).join(', ') || fallback;
  return fallback;
}

function valueFor(record: DetailRecord, ...keys: string[]): unknown {
  return keys.map((key) => record[key]).find((value) => value !== undefined && value !== null && value !== '');
}

function recordsFor(record: DetailRecord, ...keys: string[]): DetailRecord[] {
  const value = valueFor(record, ...keys);
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function codesFor(record: DetailRecord, ...keys: string[]): string {
  const value = valueFor(record, ...keys);
  if (!Array.isArray(value)) return scalarText(value);
  return value.map((code) => isRecord(code)
    ? [scalarText(valueFor(code, 'code', 'value', 'id'), ''), scalarText(valueFor(code, 'description', 'short_description', 'name'), '')].filter(Boolean).join(' — ')
    : scalarText(code, '')).filter(Boolean).join(', ') || '—';
}

function boundedPreview(value: unknown, maximum = 5000): string {
  const text = scalarText(value, '');
  return text.length > maximum ? `${text.slice(0, maximum)}\n\nPreview truncated.` : text;
}

type KeyValue = { label: string; value: unknown; key?: string };

const ReadOnlyKeyValues: React.FC<{ items: KeyValue[] }> = ({ items }) => (
  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
    {items.filter((item) => item.value !== undefined && item.value !== null && item.value !== '').map((item) => (
      <Box key={item.label} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(160px, 0.35fr) 1fr' }, gap: { xs: 0, sm: 1 }, alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{item.label}</Typography>
        <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>{item.key && isSensitiveDetailKey(item.key) ? '[redacted]' : scalarText(item.value)}</Typography>
      </Box>
    ))}
  </Stack>
);

const ReadOnlyTable: React.FC<{ columns: string[]; rows: string[][]; emptyLabel: string }> = ({ columns, rows, emptyLabel }) => (
  rows.length ? <TableContainer component={Paper} variant="outlined" sx={{ mt: 0.75, bgcolor: 'background.paper' }}>
    <Table size="small" aria-label={columns.join(' ')}>
      <TableHead><TableRow>{columns.map((column) => <TableCell key={column} sx={{ fontWeight: 700 }}>{column}</TableCell>)}</TableRow></TableHead>
      <TableBody>{rows.slice(0, 25).map((row, index) => <TableRow key={`${row.join('-')}-${index}`}>{row.map((cell, cellIndex) => <TableCell key={`${cellIndex}-${cell}`} sx={{ verticalAlign: 'top', overflowWrap: 'anywhere' }}>{cell || '—'}</TableCell>)}</TableRow>)}</TableBody>
    </Table>
    {rows.length > 25 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', p: 1 }}>Showing the first 25 records.</Typography>}
  </TableContainer> : <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>{emptyLabel}</Typography>
);

function conciseFallbackItems(data: DetailRecord): KeyValue[] {
  return Object.entries(data).slice(0, 12).map(([key, value]) => ({
    label: key.replace(/_/g, ' '), key,
    value: isSensitiveDetailKey(key) ? '[redacted]' : Array.isArray(value) ? `${value.length} item${value.length === 1 ? '' : 's'}` : isRecord(value) ? `${Object.keys(value).length} field${Object.keys(value).length === 1 ? '' : 's'}` : value,
  }));
}

type QuestionAnswerPair = { prompt: string; answer: unknown; key: string };

/**
 * MR completed-stage data can be a direct controller output or a normal-flow
 * hydration envelope. Keep traversal deliberately shallow and bounded: these
 * are known storage wrappers, not arbitrary recursive JSON to display.
 */
function stageDataEnvelopes(record: DetailRecord): DetailRecord[] {
  const wrappers = ['output', 'data', 'result', 'context', 'stage_data', 'stage_output', 'input'];
  const direct = [record, ...wrappers.map((key) => record[key]).filter(isRecord)];
  return direct.filter((candidate, index) => direct.indexOf(candidate) === index);
}

function firstEnvelopeValue(envelopes: DetailRecord[], ...keys: string[]): unknown {
  for (const envelope of envelopes) {
    const value = valueFor(envelope, ...keys);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function isNonEmptyQuestionSource(value: unknown): boolean {
  return (Array.isArray(value) && value.length > 0) || (isRecord(value) && Object.keys(value).length > 0);
}

function normalizedQuestionLookup(value: unknown): string {
  return scalarText(value, '').trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function answerValue(value: unknown): unknown {
  return isRecord(value) ? valueFor(value, 'answer', 'answer_text', 'response', 'value', 'text') : value;
}

/** Normalizes canonical arrays, question maps, and persisted Q&A envelopes. */
function questionAnswerPairs(record: DetailRecord): QuestionAnswerPair[] {
  const envelopes = stageDataEnvelopes(record);
  const answers = firstEnvelopeValue(envelopes, 'answers', 'context_answers', 'question_answers', 'responses');
  const answerMap = isRecord(answers) ? answers : {};
  const answerList = Array.isArray(answers) ? answers : [];
  const questions = envelopes.map((envelope) => valueFor(envelope, 'questions', 'context_questions'))
    .find(isNonEmptyQuestionSource);
  const persistedPairs = envelopes.map((envelope) => valueFor(envelope, 'questions_and_answers', 'question_answers'))
    .find((value) => Array.isArray(value) && value.length > 0);
  const answerFor = (key: string, prompt: string, index: number, question?: DetailRecord): unknown => {
    const aliases = [
      key, prompt, String(index), String(index + 1),
      ...(question ? [
        valueFor(question, 'id'), valueFor(question, 'key'), valueFor(question, 'question_id', 'questionId'),
        valueFor(question, 'question', 'question_text', 'prompt', 'label', 'text'),
      ] : []),
    ].map(normalizedQuestionLookup).filter(Boolean);
    const mapped = Object.entries(answerMap).find(([answerKey]) => aliases.includes(normalizedQuestionLookup(answerKey)));
    if (mapped) return answerValue(mapped[1]);
    const listed = answerList.find((answer) => isRecord(answer) && aliases.some((alias) => [
      answer.id, answer.key, answer.question_id, answer.questionId, answer.question, answer.question_text, answer.prompt,
    ].map(normalizedQuestionLookup).includes(alias)));
    if (listed !== undefined) return answerValue(listed);
    const direct = answerList[index] ?? question?.answer ?? question?.value
      // A legacy string-question list has no ID. With one question and one
      // saved answer, that answer is unambiguous even when its map key differs.
      ?? (Object.keys(answerMap).length === 1 ? Object.values(answerMap)[0] : undefined);
    return answerValue(direct);
  };

  if (Array.isArray(questions)) return questions.flatMap((question, index) => {
    if (isRecord(question)) {
      const key = scalarText(valueFor(question, 'id', 'key', 'question_id'), `question-${index + 1}`);
      const prompt = scalarText(valueFor(question, 'question', 'question_text', 'prompt', 'label', 'text'), key);
      return prompt ? [{ key, prompt, answer: answerFor(key, prompt, index, question) }] : [];
    }
    const prompt = scalarText(question, '');
    return prompt ? [{ key: prompt, prompt, answer: answerFor(prompt, prompt, index) }] : [];
  });
  if (isRecord(questions)) return Object.entries(questions).map(([key, question], index) => {
    const questionRecord = isRecord(question) ? question : undefined;
    const prompt = questionRecord
      ? scalarText(valueFor(questionRecord, 'question', 'question_text', 'prompt', 'label', 'text'), key)
      : scalarText(question, key);
    return { key, prompt, answer: answerFor(key, prompt, index, questionRecord) };
  });

  if (Array.isArray(persistedPairs)) return persistedPairs.flatMap((pair, index) => {
    if (!isRecord(pair)) return [];
    const question = isRecord(pair.question) ? pair.question : undefined;
    const key = scalarText(valueFor(question || pair, 'id', 'key', 'question_id'), `question-${index + 1}`);
    const prompt = question
      ? scalarText(valueFor(question, 'question', 'question_text', 'prompt', 'label', 'text'), key)
      : scalarText(valueFor(pair, 'question', 'question_text', 'prompt', 'label', 'text'), key);
    return prompt ? [{ key, prompt, answer: answerValue(valueFor(pair, 'answer', 'response', 'value')) ?? answerFor(key, prompt, index) }] : [];
  });

  // Synopsis hydration can represent its review prompts as mapping fields.
  const mapping = envelopes.map((envelope) => envelope.field_mapping).find(isRecord) || null;
  const fields = mapping ? recordsFor(mapping, 'fields') : envelopes.flatMap((envelope) => recordsFor(envelope, 'fields'));
  return fields.map((field, index) => {
    const key = scalarText(valueFor(field, 'source_path', 'key', 'id', 'target'), `field-${index + 1}`);
    const prompt = scalarText(valueFor(field, 'question', 'question_text', 'prompt', 'label', 'target', 'source_path'), key);
    return { key, prompt, answer: answerFor(key, prompt, index, field) };
  });
}

const ReadOnlyQuestionAnswers: React.FC<{
  pairs: QuestionAnswerPair[];
  showHeading?: boolean;
  onCopyAll?: () => void;
  copyFeedback?: string | null;
}> = ({ pairs, showHeading = true, onCopyAll, copyFeedback }) => pairs.length ? <>
  {showHeading && <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.75 }}>Saved questions and answers</Typography>}
  {onCopyAll && <Box sx={{ mt: 0.75 }}>
    <Button variant="outlined" size="small" onClick={onCopyAll}>Copy Questions</Button>
    {copyFeedback && <Typography component="span" role="status" aria-live="polite" variant="caption" sx={{ display: 'block', mt: 0.5 }} data-testid="mr-chat-completed-copy-feedback">{copyFeedback}</Typography>}
  </Box>}
  <Stack spacing={0.75} sx={{ mt: 0.75 }}>
    {pairs.slice(0, 20).map((pair) => <Paper key={pair.key} variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>{pair.prompt}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.25 }}>{scalarText(pair.answer, 'No answer saved.')}</Typography>
    </Paper>)}
  </Stack>
</> : null;

function valueAtPath(record: DetailRecord, path: string): unknown {
  return path.split('.').reduce<unknown>((current, part) => isRecord(current) ? current[part] : undefined, record);
}

function synopsisValueText(value: unknown): unknown {
  if (Array.isArray(value)) return value.length ? `${value.length} line item${value.length === 1 ? '' : 's'}` : undefined;
  return value;
}

function hasSynopsisValue(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '' && (!Array.isArray(value) || value.length > 0);
}

function synopsisFieldLabel(path: string): string {
  const leaf = path.split('.').slice(-1)[0];
  return leaf?.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || path;
}

function selectedSynopsisItems(record: DetailRecord): KeyValue[] {
  const envelopes = stageDataEnvelopes(record);
  const form = envelopes.map((envelope) => valueFor(envelope, 'solicitation_stage11', 'synopsis'))
    .find(isRecord) || {};
  const instrument = scalarText(valueFor(form, 'instrument_type', 'form_type'));
  const mapping = envelopes.map((envelope) => valueFor(envelope, 'field_mapping')).find(isRecord)
    || (isRecord(form) ? valueFor(form, 'field_mapping') : undefined);
  const mappedFields = isRecord(mapping) ? recordsFor(mapping, 'fields') : [];
  const items: KeyValue[] = [{ label: 'Selected form', value: instrument }];

  if (mappedFields.length) {
    mappedFields.forEach((field) => {
      const path = scalarText(valueFor(field, 'source_path', 'key'));
      const status = scalarText(field.status);
      if (!path || status === 'offeror_completed' || status === 'post_award_blank') return;
      const value = valueAtPath(form, path);
      if (!hasSynopsisValue(value)) return;
      items.push({ label: scalarText(valueFor(field, 'target', 'label'), synopsisFieldLabel(path)), value: synopsisValueText(value), key: path });
    });
    return items;
  }

  // Completed cards normally persist the canonical typed state, not a copy of
  // the transient field mapping. In that shape, resolve only shared decisions,
  // core administrative values, and the selected instrument namespace.
  ['selection_rationale', 'authority_attestation'].forEach((key) => {
    const value = form[key];
    if (hasSynopsisValue(value)) items.push({ label: synopsisFieldLabel(key), value, key });
  });
  const administrative = isRecord(valueAtPath(form, 'core.administrative')) ? valueAtPath(form, 'core.administrative') as DetailRecord : {};
  Object.entries(administrative).forEach(([key, value]) => {
    if (hasSynopsisValue(value)) items.push({ label: synopsisFieldLabel(key), value: synopsisValueText(value), key: `core.administrative.${key}` });
  });
  const schedule = valueAtPath(form, 'core.schedule');
  if (hasSynopsisValue(schedule)) items.push({ label: 'Schedule', value: synopsisValueText(schedule), key: 'core.schedule' });
  const details = isRecord(valueAtPath(form, `instrument_details.${instrument}`))
    ? valueAtPath(form, `instrument_details.${instrument}`) as DetailRecord
    : {};
  Object.entries(details).forEach(([key, value]) => {
    if (hasSynopsisValue(value)) items.push({ label: synopsisFieldLabel(key), value: synopsisValueText(value), key: `instrument_details.${instrument}.${key}` });
  });
  return items;
}

/** A compact, stage-aware review of canonical data; it never offers edits. */
const CompletedStageDetails: React.FC<{
  stage: MrChatCompletedStage;
  data: unknown;
  onCopyQuestions?: (stageKey: string, pairs: QuestionAnswerPair[]) => void;
  copyFeedback?: { target: string; message: string } | null;
}> = ({ stage, data, onCopyQuestions, copyFeedback }) => {
  const record = isRecord(data) ? data : null;
  const stageKey = stage.stage_key;
  if (!record) return <Typography variant="caption" color="text.secondary">{readOnlyResultText(data)}</Typography>;
  const questionPairs = questionAnswerPairs(record);

  if (stageKey === 'context_questions') {
    return <>
      <ReadOnlyKeyValues items={[
        { label: 'Items detected', value: record.num_items_detected },
      ]} />
    <ReadOnlyQuestionAnswers
      pairs={questionPairs}
      showHeading={false}
      onCopyAll={questionPairs.length ? () => onCopyQuestions?.(stageKey, questionPairs) : undefined}
      copyFeedback={copyFeedback?.target === `completed-${stageKey}` ? copyFeedback.message : null}
    />
    </>;
  }

  if (stageKey === 'salient_characteristics_mfr') {
    const removed = Array.isArray(record.removed_specs) ? record.removed_specs : [];
    return <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Minimum functional requirements</Typography>
      <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>{boundedPreview(valueFor(record, 'minimum_requirements', 'requirements_text', 'original_spec'))}</Typography>
      <ReadOnlyKeyValues items={[
        { label: 'Requirements retained', value: Array.isArray(record.requirements_list) ? `${record.requirements_list.length} item(s)` : undefined },
        { label: 'Cost savings estimate', value: record.cost_savings_estimate },
      ]} />
      {removed.length > 0 && <><Typography variant="body2" sx={{ fontWeight: 600, mt: 1.25 }}>Removed overspecifications</Typography><Stack spacing={0.75} sx={{ mt: 0.75 }}>
        {removed.slice(0, 20).map((entry, index) => {
          const item = isRecord(entry) ? entry : null;
          const specification = item ? scalarText(valueFor(item, 'specification', 'original_spec', 'requirement', 'text', 'description')) : scalarText(entry);
          const reason = item ? scalarText(valueFor(item, 'reason', 'explanation', 'rationale', 'why_removed'), '') : '';
          return <Paper key={`${specification}-${index}`} variant="outlined" sx={{ p: 1, bgcolor: 'background.paper' }}>
            <Typography variant="body2">{specification}</Typography>
            {reason && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>{reason}</Typography>}
          </Paper>;
        })}
      </Stack></>}
    </>;
  }

  if (stageKey === 'product_discovery') {
    const products = recordsFor(record, 'selected_products', 'products');
    const sourceStatus = isRecord(record.source_status) ? record.source_status : {};
    return <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Products selected</Typography>
      <ReadOnlyTable columns={['Product', 'Manufacturer', 'Model', 'Source', 'Evidence']} rows={products.map((product) => [
        scalarText(valueFor(product, 'product_name', 'name', 'title', 'description')),
        scalarText(valueFor(product, 'manufacturer', 'brand')),
        scalarText(valueFor(product, 'model', 'part_number', 'sku')),
        scalarText(valueFor(product, 'source', 'source_name')),
        scalarText(valueFor(product, 'evidence', 'evidence_url', 'url', 'source_url')),
      ])} emptyLabel="No product records were included in this saved result." />
      <Typography variant="body2" sx={{ fontWeight: 600, mt: 1.25 }}>Discovery source evidence</Typography>
      <ReadOnlyKeyValues items={[
        { label: 'Sources searched', value: Array.isArray(record.sources) ? record.sources.join(', ') : record.sources },
        ...Object.entries(sourceStatus).slice(0, 10).map(([source, status]) => ({ label: source, value: status, key: source })),
      ]} />
      {isRecord(record.source_results) && Array.isArray(record.source_results.items) &&
        <ProductProgramEvidence results={record.source_results as unknown as ProductSourceResults} />}
    </>;
  }

  if (stageKey === 'psc' || stageKey === 'naics') {
    const products = recordsFor(record, stageKey === 'psc' ? 'products_with_psc' : 'products_with_naics');
    const codeLabel = stageKey === 'psc' ? 'PSC' : 'NAICS';
    return <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{codeLabel} mappings</Typography>
      <ReadOnlyTable columns={['Product', codeLabel, 'Description']} rows={products.map((product) => {
        const codes = stageKey === 'psc'
          ? codesFor(product, 'psc_codes', 'pscCode', 'psc_code')
          : codesFor(product, 'naics_from_psc', 'naics_codes', 'naicsCode', 'naics_code');
        return [
          scalarText(valueFor(product, 'product_name', 'name', 'title')),
          codes,
          scalarText(valueFor(product, stageKey === 'psc' ? 'psc_description' : 'naics_description', 'description')),
        ];
      })} emptyLabel={`No ${codeLabel} mappings were included in this saved result.`} />
      {stageKey === 'naics' && record.requirements_summary && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.75 }}>{boundedPreview(record.requirements_summary, 1800)}</Typography>}
    </>;
  }

  if (stageKey === 'vendor_discovery') {
    const products = recordsFor(record, 'products_with_vendors');
    const selectedRows = products.flatMap((product) => recordsFor(product, 'selected_vendor_records').map((vendor) => [
      scalarText(valueFor(product, 'product_name', 'name', 'title')),
      scalarText(valueFor(vendor, 'name', 'vendor_name', 'company_name')),
      scalarText(valueFor(vendor, 'cage_code', 'cage', 'uei')),
    ]));
    const vendorRows = products.flatMap((product) => {
      const productName = scalarText(valueFor(product, 'product_name', 'name', 'title'));
      const records = recordsFor(product, 'vendor_records');
      const vendors = Array.isArray(product.vendors) ? product.vendors : [];
      return records.length ? records.map((vendor) => [
        productName,
        scalarText(valueFor(vendor, 'name', 'vendor_name', 'company_name')),
        scalarText(valueFor(vendor, 'cage_code', 'cage', 'uei')),
        scalarText(valueFor(vendor, 'business_type_display', 'business_type', 'business_types')),
        scalarText(valueFor(vendor, 'website', 'url', 'email')),
      ]) : vendors.map((vendor) => [
        productName,
        isRecord(vendor) ? scalarText(valueFor(vendor, 'name', 'vendor_name', 'company_name')) : scalarText(vendor),
        isRecord(vendor) ? scalarText(valueFor(vendor, 'cage_code', 'cage', 'uei')) : '—',
        isRecord(vendor) ? scalarText(valueFor(vendor, 'business_type_display', 'business_type', 'business_types')) : '—',
        isRecord(vendor) ? scalarText(valueFor(vendor, 'website', 'url', 'email')) : '—',
      ]);
    });
    const selectedVendors = recordsFor(record, 'selected_vendors');
    const savedSelectedRows = selectedRows.length ? selectedRows : selectedVendors.map((vendor) => [
      '—',
      scalarText(valueFor(vendor, 'name', 'vendor_name', 'company_name')),
      scalarText(valueFor(vendor, 'cage_code', 'cage', 'uei')),
    ]);
    const automaticallySelected = record.vendor_selection_policy === 'automatic_top_vendors';
    return <>
      {automaticallySelected && <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Top vendors were selected automatically for Sections B–E. You can change these selections when you enter the workflow.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Selected for Sections B–E</Typography>
        <ReadOnlyTable columns={['Product', 'Vendor', 'CAGE / UEI']} rows={savedSelectedRows} emptyLabel="No vendors were selected." />
        {display(record.vendor_selection_notice) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {display(record.vendor_selection_notice)}
        </Typography>}
      </>}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Vendors identified</Typography>
      <ReadOnlyTable columns={['Product', 'Vendor', 'CAGE / UEI', 'Business type', 'Contact / evidence']} rows={vendorRows.length ? vendorRows : selectedVendors.map((vendor) => [
        '—', scalarText(valueFor(vendor, 'name', 'vendor_name', 'company_name')), scalarText(valueFor(vendor, 'cage_code', 'cage', 'uei')),
        scalarText(valueFor(vendor, 'business_type_display', 'business_type', 'business_types')), scalarText(valueFor(vendor, 'website', 'url', 'email')),
      ])} emptyLabel="No vendor records were included in this saved result." />
    </>;
  }

  if (stageKey === 'brand_name_ja') {
    const artifact = isRecord(record.artifact) ? record.artifact : isRecord(record.brand_name_ja) ? record.brand_name_ja : record;
    const route = isRecord(record.justification_route) ? record.justification_route : isRecord(artifact.justification_route) ? artifact.justification_route : {};
    const body = typeof artifact.document_body === 'string' ? artifact.document_body : '';
    return <>
      {record.context_notice && <Alert severity="info" sx={{ mb: 1 }}>{display(record.context_notice)}</Alert>}
      {(artifact.publication_notice || record.publication_notice) && <Alert severity="info" sx={{ mb: 1 }}>{display(artifact.publication_notice || record.publication_notice)}</Alert>}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Brand-name J&amp;A</Typography>
      {route.authority_text && <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>{display(route.authority_text)}</Typography>}
      {body ? <Box data-testid="mr-chat-markdown-brand_name_ja" sx={{ mt: 1, overflowWrap: 'anywhere' }}><Typography sx={{ whiteSpace: 'pre-wrap' }}>{boundedPreview(plainJustificationText(body))}</Typography></Box>
        : <Typography variant="caption">No professional document is saved for this route. Retained answers and official actions remain available in the workflow.</Typography>}
    </>;
  }

  if (['section_b', 'section_c', 'section_d', 'section_e'].includes(stageKey)) {
    const envelopes = stageDataEnvelopes(record);
    const content = firstEnvelopeValue(envelopes, `${stageKey}_content`, stageKey, 'content', 'document');
    const metadata = firstEnvelopeValue(envelopes, 'metadata');
    const reviewMetadata = Object.entries(isRecord(metadata) ? metadata : {})
      // The saved answers are already rendered above. Counts confirming that
      // generation received them (or the product count it used) add noise to
      // a completed-stage review and are not user decisions.
      .filter(([key]) => !['context_answers_provided', 'context_answers_count', 'product_count', 'generation_successful', 'vendor_selection_fingerprint', 'vendor_selection_stale', 'section_d_content_digest'].includes(key))
      .slice(0, 8)
      .map(([key, value]) => ({ label: key.replace(/_/g, ' '), value, key }));
    return <>
    {record.vendor_selection_stale === true && <VendorSelectionStaleWarning testId={`mr-chat-${stageKey}-vendor-selection-stale`} />}
    {stageKey === 'section_d' && record.research_evidence_stale === true && (
      <Alert severity="warning" sx={{ mb: 1 }} data-testid="mr-chat-section_d-research-evidence-stale">
        This saved draft predates the current research evidence or has no saved evidence record. Its checklist does not establish current research execution. Review or regenerate Section D in the workflow to use the current findings.
      </Alert>
    )}
    <ReadOnlyQuestionAnswers
      pairs={questionPairs}
      onCopyAll={questionPairs.length ? () => onCopyQuestions?.(stageKey, questionPairs) : undefined}
      copyFeedback={copyFeedback?.target === `completed-${stageKey}` ? copyFeedback.message : null}
    />
      <Typography variant="body2" sx={{ fontWeight: 600, mt: questionPairs.length ? 1.25 : 0 }}>
        {`Generated ${display(stage.label, stageKey)} document`}
      </Typography>
      <ReadOnlyKeyValues items={reviewMetadata} />
      {content && <Box sx={{ mt: 1, overflowWrap: 'anywhere' }} data-testid={`mr-chat-markdown-${stageKey}`}><AssistantMarkdown text={boundedPreview(content)} suppressRemoteImages /></Box>}
      {firstEnvelopeValue(envelopes, 'tbd_items') && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>Saved TBD items remain available for workflow review.</Typography>}
    </>;
  }

  if (stageKey === 'synopsis') {
    const handoff = asMarketResearchHandoff(firstEnvelopeValue(stageDataEnvelopes(record), 'handoff'));
    const items = selectedSynopsisItems(record);
    // Older completed chats stored user review prompts separately from the
    // typed Stage 11 state. Retain those real answers only when no canonical
    // mapped values are available; never use mapping rows as fake questions.
    const legacyPairs = items.length <= 1
      ? questionPairs.filter((pair) => hasSynopsisValue(answerValue(pair.answer)))
      : [];
    return <>
      {handoff && <><MarketResearchHandoffStatus handoff={handoff} /><MarketResearchOfficialActionStatus handoff={handoff} /></>}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Synopsis form summary</Typography>
      <ReadOnlyQuestionAnswers pairs={legacyPairs} />
      <ReadOnlyKeyValues items={items} />
    </>;
  }

  if (stageKey === 'vendor_bids_abstract') {
    const bids = recordsFor(record, 'bids', 'vendor_bids');
    return <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>Vendor bid summary</Typography>
      <ReadOnlyTable columns={['Vendor', 'Quote number', 'Quote date', 'Total amount', 'Applicable']} rows={bids.map((bid) => [
        scalarText(valueFor(bid, 'vendor_name', 'vendor', 'name')),
        scalarText(valueFor(bid, 'quote_number', 'quote_id')),
        scalarText(valueFor(bid, 'quote_date', 'date')),
        scalarText(valueFor(bid, 'total_amount', 'amount', 'total')),
        scalarText(valueFor(bid, 'applicable', 'is_applicable')),
      ])} emptyLabel="No extracted vendor bids were included in this saved result." />
      <ReadOnlyKeyValues items={Object.entries(isRecord(record.metadata) ? record.metadata : {}).slice(0, 8).map(([key, value]) => ({ label: key.replace(/_/g, ' '), value, key }))} />
      {record.abstract && <><Typography variant="body2" sx={{ fontWeight: 600, mt: 1.25 }}>Abstract of offers</Typography><Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>{boundedPreview(record.abstract)}</Typography></>}
    </>;
  }

  return <><Typography variant="body2" sx={{ fontWeight: 600 }}>Saved stage summary</Typography><ReadOnlyKeyValues items={conciseFallbackItems(record)} /></>;
};

function currentOperationText(progress: MrChatSessionView['progress']): string {
  const current = progress?.current_step || progress?.current_operation;
  return typeof current === 'string'
    ? current
    : display(current?.message) || display(current?.detail) || display(current?.label);
}

function progressText(progress: MrChatSessionView['progress']): string {
  const currentText = currentOperationText(progress);
  return currentText
    || display(progress?.message) || display(progress?.label)
    || display(progress?.pending_context?.label)
    || (progress?.steps || []).map((step) => step.state === 'running'
      ? display(step.message) || display(step.detail) || display(step.label)
      : '').find(Boolean)
    || '';
}

function progressIsCompleted(progress: MrChatSessionView['progress']): boolean {
  const phase = display(progress?.phase).toLowerCase();
  const message = `${display(progress?.message)} ${display(progress?.label)}`.toLowerCase();
  return phase.endsWith('_completed') || /(^|\s)completed\b/.test(message);
}

function controllerActivityLabel(view: MrChatSessionView | null, pending: MrChatPendingInput | null): string {
  // The persisted completion notification describes the stage that just
  // finished. Once the controller has issued the next pending contract it is
  // stale, so orient the user to that next cursor instead.
  if (!progressIsCompleted(view?.progress)) {
    const operation = progressText(view?.progress);
    if (operation) return operation;
  }
  const stageLabel = display(view?.current_stage?.label) || display(pending?.stage_key).replace(/_/g, ' ');
  if (stageLabel) return `Preparing ${stageLabel}`;
  // This is the only pre-response shell: the server has accepted an MR
  // selection but has not returned its controller view yet. Its next action
  // is always the Stage 1 context-question contract, so name that work
  // directly instead of showing a generic opening label.
  return 'Generating Context Questions.';
}

function handoffAction(handoff: MrChatWorkflowHandoff | null | undefined): { flowId: string; stage?: string; terminal: boolean } | null {
  if (handoff?.available !== true || typeof handoff.flow_id !== 'string' || !handoff.flow_id) return null;
  return {
    flowId: handoff.flow_id,
    stage: typeof handoff.target_stage_key === 'string' ? handoff.target_stage_key : undefined,
    terminal: handoff.terminal === true || handoff.mode === 'complete' || handoff.mode === 'review',
  };
}

/**
 * A progress 409 is normally an expected controller lifecycle transition,
 * rather than a request the browser can retry forever. Keep this deliberately
 * narrow: ordinary 409s still follow their established error handling.
 */
function isRecoverableOperationConflict(cause: unknown): boolean {
  if (!cause || typeof cause !== 'object') return false;
  const error = cause as { status?: unknown; message?: unknown; body?: unknown };
  if (error.status !== 409) return false;
  const body = error.body && typeof error.body === 'object' ? error.body as Record<string, unknown> : {};
  const detail = body.detail && typeof body.detail === 'object' ? body.detail as Record<string, unknown> : {};
  const signals = [error.message, body.code, body.error_code, detail.code, detail.error_code]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
  return /refresh_required|takeover_required|operation[_ -]?(?:stopped|expired|conflict)|execution[_ -]?(?:stopped|expired|conflict)|lease[_ -]?(?:expired|lost)/.test(signals);
}

type Props = {
  initialView?: MrChatSessionView | null;
  onTurn?: (input: MrChatTurnInput) => Promise<MrChatSessionView>;
  /** Owner-scoped MR controller polling, supplied by Home Chat (never v2 progress). */
  onPollView?: (pollNumber: number) => Promise<MrChatSessionView>;
  /** Rehydrate the bound controller after one typed, terminal poll conflict. */
  onRehydrate?: () => Promise<MrChatSessionView>;
  onOpenWorkflow?: (flowId: string, targetStageHint?: string) => void;
  /** Execute only the currently-issued Section C controller action. */
  onSectionCHandoff?: () => Promise<MrChatSessionView>;
  /** Controller-bound Questions mutations; the panel never sends a flow/source authority. */
  onNotifyQuestions?: (requestId: string) => Promise<MarketResearchNotificationRequestResult>;
  onShareAndNotifyQuestions?: (emails: string[], requestId: string) => Promise<MarketResearchNotificationRequestResult>;
  /** Upload is deliberately injected by Home Chat; file bytes never enter transcript state. */
  onUploadFiles?: (files: File[]) => Promise<string[]>;
  rehydrating?: boolean;
  processingRequestId?: string | null;
  /** Original Home Chat query while the MR controller view is still loading. */
  initiatingRequest?: string | null;
  /** Home Chat's owner-scoped cancellation affordance, colocated with busy work. */
  processingAction?: React.ReactNode;
  /** Main Home Chat's launcher-owned utility, never supplied by a companion. */
  headerAction?: React.ReactNode;
  /** Read-only Home Chat transcript preceding this controller. */
  previousConversation?: React.ReactNode;
};

type ViewApplySource = 'external' | 'turn' | 'poll' | 'rehydrate';

type ViewApplyOptions = {
  source?: ViewApplySource;
  pollNumber?: number;
  /** State observed when an owner-bound progress request was dispatched. */
  viewGeneration?: number;
  editGeneration?: number;
};

/**
 * A focused review-and-progress panel for Commodities Market Research. The
 * server controls the cursor and stage records; this component only renders
 * completed work and submits its currently-issued pending-input contract.
 */
const MrChatPanel: React.FC<Props> = ({
  initialView = null,
  onTurn,
  onPollView,
  onRehydrate,
  onOpenWorkflow,
  onSectionCHandoff,
  onNotifyQuestions,
  onShareAndNotifyQuestions,
  onUploadFiles,
  rehydrating = false,
  processingRequestId = null,
  initiatingRequest = null,
  processingAction,
  headerAction,
  previousConversation,
}) => {
  const [view, setView] = useState<MrChatSessionView | null>(initialView);
  const [values, setValues] = useState<MrChatAnswerMap>(() => initialValues(initialView?.pending_input));
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [sectionCHandoffBusy, setSectionCHandoffBusy] = useState(false);
  const [localOperation, setLocalOperation] = useState<string | null>(null);
  const [writerRefreshBusy, setWriterRefreshBusy] = useState(false);
  const [writerRefreshFailed, setWriterRefreshFailed] = useState(false);
  const [pollRecovery, setPollRecovery] = useState<'idle' | 'rehydrating' | 'failed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<{ target: string; message: string } | null>(null);
  const [questionsShareOpen, setQuestionsShareOpen] = useState(false);
  const [expandedCompletedStages, setExpandedCompletedStages] = useState<Record<string, boolean>>({});
  const [mrrExpanded, setMrrExpanded] = useState(false);
  const [mrrOpened, setMrrOpened] = useState(false);
  const mrrRef = useRef<MarketResearchMrrDetailsHandle | null>(null);
  const mrPollNumberRef = useRef(0);
  const lastAppliedMrPollRef = useRef(0);
  const currentFormIdentityRef = useRef(pendingFormIdentity(initialView));
  // A retained panel may receive another Home Chat session before an earlier
  // poll, turn, or recovery resolves.
  const selectedMrSessionIdRef = useRef(initialView?.session_id);
  selectedMrSessionIdRef.current = initialView?.session_id;
  const recoveryOwnerSessionRef = useRef(initialView?.session_id);
  const dirtyFormIdentityRef = useRef<string | null>(null);
  const localEditGenerationRef = useRef(0);
  const viewGenerationRef = useRef(0);
  // One conflict outcome gets one recovery attempt. A new accepted turn is a
  // new operation and explicitly resets this fence below.
  const recoveredPollConflictRef = useRef<string | null>(null);
  const recoveryResumeTimerRef = useRef<number | null>(null);
  const autoSourceResumeKeyRef = useRef<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const conversationRef = useRef<HTMLDivElement | null>(null);
  const pending = view?.pending_input || null;
  // A controller view can arrive after its Home Chat intent-selection request
  // has settled. Its live operation is authoritative on its own: do not hide
  // it behind the generic opening label merely because no request id remains.
  const serverProgressLabel = !progressIsCompleted(view?.progress) ? currentOperationText(view?.progress) : '';
  // `pending_context` is a ready-to-answer form state, not work in progress.
  // It may carry an instructional label, but it must never keep the form
  // hidden after an owner-bound poll has returned its fields.
  const hasLiveProgress = Boolean(serverProgressLabel);
  const completed = useMemo(() => (view?.completed_stages || [])
    .map((stage, index) => ({ stage, index, order: typeof stage.display_order === 'number' ? stage.display_order : index }))
    .sort((left, right) => left.order - right.order || left.index - right.index)
    .map(({ stage }) => stage), [view?.completed_stages]);
  const controllerViewKey = `${view?.session_id || ''}:${display(view?.current_stage?.key)}:${completed.map(completedStageKey).join('|')}:${display(pending?.stage_key)}:${display(pending?.prompt)}`;
  const durableInitiatingRequest = initiatingRequestText(view) || (initiatingRequest || '').trim();
  const entryAcknowledgement = display(view?.entry_acknowledgement).trim();
  const conversationMessages = useMemo(() => {
    let initiatingMessageConsumed = false;
    const visibleMessages = (view?.messages || []).filter((message) => {
      if (message.visible_in_chat === false) return false;
      // The controller persists the initiating user turn both as its named
      // durable request and in the regular transcript. Render the named
      // record once (above), not a second time from the transcript.
      if (!initiatingMessageConsumed && durableInitiatingRequest && message.role === 'user'
        && display(message.content).trim() === durableInitiatingRequest) {
        initiatingMessageConsumed = true;
        return false;
      }
      return true;
    });
    return withoutActiveStagePrompt(visibleMessages, pending?.prompt);
  }, [durableInitiatingRequest, view?.messages, pending]);
  const isContextQuestions = pending?.kind === 'context_questions';
  const isSynopsisForm = pending?.kind === 'synopsis_form' || pending?.stage_key === 'synopsis';
  const mrrFlowId = typeof view?.workflow?.flow_id === 'string' && view.workflow.flow_id.trim()
    ? view.workflow.flow_id : null;
  const mrrViewOnly = view?.controller?.state === 'refresh_required' || view?.workflow?.read_only !== false;
  const canShowMrr = Boolean(mrrFlowId) && view?.recovery?.kind !== 'product_discovery_empty';
  const pendingFormId = `mr-chat-pending-${view?.session_id || 'session'}`;
  const synopsisSelector = useMemo(() => (pending?.fields || []).find(isSynopsisFormSelector)
    || synopsisFormTypeField(pending), [pending]);
  const selectedSynopsisForm = isSynopsisForm
    ? display(values[synopsisSelector ? fieldKey(synopsisSelector) : ''] ?? pending?.selected_form)
    : '';
  const interactiveFields = useMemo(() => isSynopsisForm
    ? synopsisInteractiveFields(pending, selectedSynopsisForm)
    : (pending?.fields || []).filter((field) => field.visible !== false && Boolean(fieldKey(field))),
  [isSynopsisForm, pending, selectedSynopsisForm]);
  const missingRequiredFields = interactiveFields.filter((field) => fieldIsRequired(field)
    && !['file', 'files'].includes(fieldInputType(field))
    && isMissingAnswer(values[fieldKey(field)]));
  const missingRequiredLineItemFields = missingRequiredLineItemCells(
    interactiveFields,
    values,
  );
  // A stale terminal envelope must never replace an active pending form. In
  // particular, filling local required Synopsis fields is not completion: the
  // controller still has to persist and validate the selected canonical form.
  const sectionCHandoffAction = (view?.status === 'section_c_handoff_required'
    && (view?.recovery_actions || []).find((item) => (
      item?.id === 'handoff_to_contracting' && typeof item.label === 'string'
    ))) || null;
  const sectionCHandoffState = display(view?.section_c_handoff?.state);
  const sectionCHandoffMessage = display(view?.section_c_handoff?.message);
  const sectionCFlowId = typeof view?.workflow?.flow_id === 'string'
    ? view.workflow.flow_id : undefined;
  // Section C has its own explicit, server-owned action. It is not a generic
  // terminal workflow navigation and must never suggest Stage D+ access.
  const action = pending || sectionCHandoffAction || sectionCHandoffState
    ? null : handoffAction(view?.workflow_handoff);

  const applyView = useCallback((next: MrChatSessionView, options: ViewApplyOptions = {}) => {
    const source = options.source || 'external';
    if (source !== 'external' && next.session_id !== selectedMrSessionIdRef.current) return;
    const currentIdentity = currentFormIdentityRef.current;
    // While automatic work is running, progress responses intentionally omit
    // `pending_input`. That is a progress-only update, not a withdrawal of
    // the form the user just submitted.
    const pollProgressOnly = source === 'poll' && !next.pending_input && Boolean(currentIdentity);
    const nextIdentity = pollProgressOnly ? currentIdentity : pendingFormIdentity(next);

    if (source === 'poll') {
      // Poll requests may resolve out of order or after a turn has moved the
      // controller. Never let an older response replace newer local state.
      if ((options.pollNumber || 0) <= lastAppliedMrPollRef.current
        || options.viewGeneration !== viewGenerationRef.current
        || options.editGeneration !== localEditGenerationRef.current) return;
      // The server cannot issue a different question contract while this
      // browser still has unsaved input for the current one. A delayed poll
      // must wait for the authoritative turn response instead.
      if (currentIdentity && nextIdentity !== currentIdentity
        && dirtyFormIdentityRef.current === currentIdentity) return;
      lastAppliedMrPollRef.current = options.pollNumber || lastAppliedMrPollRef.current;
    } else {
      // Invalidate all poll responses dispatched before an initial hydration
      // refresh or an authoritative turn response.
      viewGenerationRef.current += 1;
    }

    const formChanged = nextIdentity !== currentIdentity;
    setView((current) => ({
      ...next,
      ...(pollProgressOnly && current?.pending_input ? { pending_input: current.pending_input } : {}),
      ...(pollProgressOnly && !next.current_stage && current?.current_stage ? { current_stage: current.current_stage } : {}),
      // A returned/polled view with a new pending contract has already
      // advanced past its `*_completed` progress event. Do not carry that
      // stale completion copy into the next active-controller status.
      progress: next.pending_input && progressIsCompleted(next.progress) ? null : next.progress,
      completed_stages: current?.session_id === next.session_id
        ? mergeCompletedStages(current.completed_stages, next.completed_stages)
        : next.completed_stages,
      messages: source === 'rehydrate' && current?.session_id === next.session_id
        ? mergeMessages(current.messages, next.messages)
        : next.messages,
    }));
    if (formChanged) {
      currentFormIdentityRef.current = nextIdentity;
      dirtyFormIdentityRef.current = null;
      setValues(initialValues(next.pending_input));
      setFiles([]);
      setError(null);
      // A stage transition is an important context change for keyboard and AT users.
      requestAnimationFrame(() => headingRef.current?.focus());
    }
  }, []);

  useEffect(() => () => {
    if (recoveryResumeTimerRef.current !== null) {
      window.clearTimeout(recoveryResumeTimerRef.current);
    }
  }, []);

  const submitSectionCHandoff = useCallback(async () => {
    if (!onSectionCHandoff || sectionCHandoffBusy || !sectionCHandoffAction) return;
    setSectionCHandoffBusy(true);
    setError(null);
    try {
      applyView(await onSectionCHandoff(), { source: 'turn' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to hand off Section C right now.');
    } finally {
      setSectionCHandoffBusy(false);
    }
  }, [applyView, onSectionCHandoff, sectionCHandoffAction, sectionCHandoffBusy]);


  useEffect(() => {
    if (recoveryOwnerSessionRef.current !== initialView?.session_id) {
      recoveryOwnerSessionRef.current = initialView?.session_id;
      recoveredPollConflictRef.current = null;
      setPollRecovery('idle');
    }
    if (initialView) applyView(initialView);
  }, [applyView, initialView]);

  const refreshWriter = useCallback(async () => {
    if (!onRehydrate || writerRefreshBusy) return;
    const sessionId = view?.session_id;
    setWriterRefreshBusy(true);
    setWriterRefreshFailed(false);
    try {
      const next = await onRehydrate();
      if (selectedMrSessionIdRef.current === sessionId) applyView(next, { source: 'rehydrate' });
    } catch {
      if (selectedMrSessionIdRef.current === sessionId) setWriterRefreshFailed(true);
    } finally {
      setWriterRefreshBusy(false);
    }
  }, [applyView, onRehydrate, view?.session_id, writerRefreshBusy]);

  const recoverFromPollConflict = useCallback((cause: unknown) => {
    if (!isRecoverableOperationConflict(cause)) return false;
    const recoveryKey = `${view?.session_id || ''}:operation-conflict`;
    if (recoveredPollConflictRef.current === recoveryKey) {
      // The recovered projection immediately hit the same lifecycle outcome.
      // It is not safe to spin or rehydrate indefinitely.
      setPollRecovery('failed');
      return true;
    }
    recoveredPollConflictRef.current = recoveryKey;
    const recoveringSessionId = view?.session_id;
    // Stop rendering active work before doing I/O. This state change also
    // tears down the interval from the polling effect below.
    setPollRecovery('rehydrating');
    if (!onRehydrate) {
      setPollRecovery('failed');
      return true;
    }
    void onRehydrate().then((next) => {
      if (selectedMrSessionIdRef.current !== recoveringSessionId) return;
      applyView(next, { source: 'rehydrate' });
      // Do not batch away the stopped-spinner state with an immediately
      // resolved hydration promise. The next effect run decides whether this
      // server projection is actually running and therefore worth polling.
      recoveryResumeTimerRef.current = window.setTimeout(() => {
        recoveryResumeTimerRef.current = null;
        setPollRecovery('idle');
      }, 0);
    }).catch(() => {
      if (selectedMrSessionIdRef.current !== recoveringSessionId) return;
      // Preserve the current transcript and offer a stable recovery surface;
      // this known lifecycle path must never become the generic error toast.
      setPollRecovery('failed');
    });
    return true;
  }, [applyView, onRehydrate, view?.session_id]);

  useEffect(() => {
    if (pollRecovery !== 'idle' || view?.controller?.state === 'refresh_required') return undefined;
    if ((!processingRequestId && !busy && !hasLiveProgress
      && view?.controller?.state !== 'waiting_for_execution'
      && view?.controller?.state !== 'observer_running') || !onPollView) return undefined;
    let cancelled = false;
    const poll = () => {
      const pollNumber = ++mrPollNumberRef.current;
      const viewGeneration = viewGenerationRef.current;
      const editGeneration = localEditGenerationRef.current;
      void onPollView(pollNumber).then((next) => {
        if (!cancelled) applyView(next, { source: 'poll', pollNumber, viewGeneration, editGeneration });
      }).catch((cause) => {
        if (cancelled || recoverFromPollConflict(cause)) return;
        const status = cause && typeof cause === 'object' ? (cause as { status?: unknown }).status : undefined;
        if (view?.controller?.state === 'observer_running' && (status === 403 || status === 404)) {
          // Access loss is terminal for an observer: discard all previously
          // rendered cards and stop polling without a generic error toast.
          setView(null);
          setPollRecovery('failed');
          return;
        }
        // The controller request itself owns user-facing failures. A transient
        // progress poll must not replace the durable view with an error.
      });
    };
    poll();
    const interval = window.setInterval(poll, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [applyView, busy, hasLiveProgress, onPollView, pollRecovery, processingRequestId,
    recoverFromPollConflict, view?.controller?.state]);

  // A rehydrated session should begin as a compact progress review. Keep
  // expansions while that same session advances, but do not carry them to a
  // different Home Chat session that happens to share a stage key.
  useEffect(() => {
    setExpandedCompletedStages({});
  }, [initialView?.session_id]);

  useEffect(() => {
    setMrrExpanded(false);
    setMrrOpened(false);
  }, [view?.session_id, mrrFlowId]);

  useEffect(() => {
    // Preserve a CO/CS's typing position. Otherwise keep the newest
    // controller result in view as stages complete or a new form arrives.
    // Assign the inner scroller directly so reopening a saved conversation
    // cannot pull the entire Home page down to the bottom of this panel.
    if (formRef.current?.contains(document.activeElement)) return;
    const conversation = conversationRef.current;
    if (conversation) conversation.scrollTop = conversation.scrollHeight;
  }, [controllerViewKey]);

  const selectSynopsisForm = useCallback((formType: string) => {
    const formIdentity = currentFormIdentityRef.current;
    if (formIdentity) dirtyFormIdentityRef.current = formIdentity;
    localEditGenerationRef.current += 1;
    setValues((current) => {
      const selectorKey = synopsisSelector ? fieldKey(synopsisSelector) : 'instrument_type';
      const next = { ...current, [selectorKey]: formType };
      // Populate fields newly revealed by the selected canonical mapping.
      // Keep a prior value if this user switches away and back, but submission
      // is restricted to the currently applicable fields below.
      synopsisInteractiveFields(pending, formType).forEach((field) => {
        const key = fieldKey(field);
        if (!key || current[key] !== undefined) return;
        const value = initialFieldValue(field);
        if (value !== undefined) next[key] = value;
      });
      return next;
    });
  }, [pending, synopsisSelector]);

  const updateValues = useCallback((updater: React.SetStateAction<MrChatAnswerMap>) => {
    const formIdentity = currentFormIdentityRef.current;
    if (formIdentity) dirtyFormIdentityRef.current = formIdentity;
    localEditGenerationRef.current += 1;
    setValues(updater);
  }, []);

  const submit = useCallback(async (actionInput?: 'start_over' | 'resume') => {
    if (!view?.session_id || !onTurn || busy) return;
    if (!actionInput && missingRequiredFields.length) {
      return;
    }
    if (!actionInput && missingRequiredLineItemFields.length) {
      return;
    }
    if (!actionInput && (pending?.upload_required || interactiveFields.some((field) => ['file', 'files'].includes(fieldInputType(field)))) && files.length === 0) {
      setError('Upload the requested quote file before continuing.');
      return;
    }
    if (!actionInput && files.length > 0 && !onUploadFiles) {
      setError('Vendor quote upload is unavailable. Please try again.');
      return;
    }
    setBusy(true);
    recoveredPollConflictRef.current = null;
    setPollRecovery('idle');
    setLocalOperation(files.length ? 'Uploading vendor quote PDFs.' : null);
    // A poll already in flight cannot supersede this newly submitted turn.
    viewGenerationRef.current += 1;
    setError(null);
    setCopyFeedback(null);
    try {
      if (!actionInput && isSynopsisForm) await mrrRef.current?.flushPendingSaves();
      const uploaded = files.length && onUploadFiles ? await onUploadFiles(files) : [];
      if (uploaded.length) setLocalOperation('Extracting vendor quote data.');
      const input = actionInput ? undefined : {
        ...(isContextQuestions ? { answers: valuesForFields(values, interactiveFields) } : valuesForFields(values, interactiveFields)),
        ...(uploaded.length ? { uploaded_files: uploaded } : {}),
      };
      applyView(await onTurn({ client_request_id: requestId(), ...(input ? { input } : {}), ...(actionInput ? { action: actionInput } : {}) }), { source: 'turn' });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unable to save that response.');
    } finally {
      setBusy(false);
      setLocalOperation(null);
    }
  }, [applyView, busy, files, interactiveFields, isContextQuestions, isSynopsisForm,
    missingRequiredFields, missingRequiredLineItemFields, onTurn, onUploadFiles,
    pending?.upload_required, values, view?.session_id]);

  useEffect(() => {
    if (view?.recovery?.kind !== 'source_plan_review_required'
      || view.recovery.auto_resume !== true || !view.session_id || !onTurn
      || view.controller?.state !== 'ready'
      || busy || rehydrating || Boolean(processingRequestId)) return;
    const key = `${view.session_id}:${view.source_plan_state?.generation || 'saved-source-review'}`;
    if (autoSourceResumeKeyRef.current === key) return;
    autoSourceResumeKeyRef.current = key;
    void submit('resume');
  }, [busy, onTurn, processingRequestId, rehydrating, submit,
    view?.recovery?.kind, view?.recovery?.auto_resume,
    view?.controller?.state, view?.session_id, view?.source_plan_state?.generation]);

  const copyQuestions = useCallback(async (target: string, questions: string[]) => {
    const text = questions.map((question, index) => `${index + 1}. ${question}`).join('\n');
    if (!text || !navigator.clipboard?.writeText) {
      setCopyFeedback({ target, message: 'Unable to copy questions to the clipboard.' });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyFeedback({ target, message: 'Questions copied to clipboard.' });
    } catch {
      setCopyFeedback({ target, message: 'Unable to copy questions to the clipboard.' });
    }
  }, []);

  const copyCompletedQuestions = useCallback((stageKey: string, pairs: QuestionAnswerPair[]) => (
    copyQuestions(`completed-${stageKey}`, pairs.map((pair) => pair.prompt))
  ), [copyQuestions]);

  const renderMrrAccordion = () => canShowMrr && mrrFlowId ? (
    <Accordion
      expanded={mrrExpanded}
      onChange={(_, expanded) => {
        setMrrExpanded(expanded);
        if (expanded) setMrrOpened(true);
      }}
      disableGutters
      elevation={0}
      sx={{ mt: 2, border: 1, borderColor: 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}
      data-testid="mr-chat-streamlined-mrr-section"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="mr-chat-streamlined-mrr-content" id="mr-chat-streamlined-mrr-toggle">
        <Typography variant="body2" sx={{ fontWeight: 700 }}>Streamlined MRR Review &amp; Completion</Typography>
      </AccordionSummary>
      <AccordionDetails id="mr-chat-streamlined-mrr-content" sx={{ p: 1.25 }}>
        {mrrOpened && <MarketResearchMrrDetailsPanel
          key={`${view?.session_id}:${mrrFlowId}`}
          ref={mrrRef}
          flowId={mrrFlowId}
          embedded
          isViewOnly={mrrViewOnly}
        />}
      </AccordionDetails>
    </Accordion>
  ) : null;

  const isBusy = busy || rehydrating || Boolean(processingRequestId)
    || (hasLiveProgress && pollRecovery === 'idle');
  const productResearchActive = view?.current_stage?.key === 'product_discovery'
    && isBusy && view.source_plan_state?.operation_kind !== 'source_re_evaluation';
  const waitingForExecution = view?.controller?.state === 'waiting_for_execution';
  const controllerReadOnly = view?.controller?.state === 'read_only';
  const observerRunning = view?.controller?.state === 'observer_running';
  const observerIdle = view?.controller?.state === 'idle_readonly';
  const interrupted = view?.controller?.state === 'interrupted'
    || view?.recovery?.kind === 'execution_interrupted';
  // An observer is allowed to consume only the server's live progress/timeline
  // projection. Treat it as stricter than regular view-only state: no forms,
  // notifications, recovery actions, contracting handoff, or workflow route.
  const writerRefreshRequired = view?.controller?.state === 'refresh_required';
  const observerReadOnly = writerRefreshRequired || controllerReadOnly || observerRunning || observerIdle
    || (interrupted && view?.recovery?.can_resume !== true);
  // The server projection is the positive authorization signal. The local
  // predicates merely make a stale/mismatched projection fail closed rather
  // than putting a Stage-1 action beside another context-question form.
  const canNotifyCustomersForQuestions = view?.questions_notification?.available === true
    && view?.questions_notification?.can_manage_share !== undefined
    && !observerReadOnly
    && view?.status !== 'complete'
    && !view?.recovery
    && view?.current_stage?.key === 'context_questions'
    && pending?.kind === 'context_questions'
    && pending?.stage_key === 'context_questions'
    && typeof view?.workflow?.flow_id === 'string';
  const canManageQuestionsShare = canNotifyCustomersForQuestions
    && view?.questions_notification?.can_manage_share === true;
  // Never replace active controller work with a generic saved-research load.
  // Poll progress wins; otherwise use the controller's current cursor/pending
  // contract to give an honest, stage-specific status.
  const busyLabel = localOperation || serverProgressLabel
    || controllerActivityLabel(view, pending);
  if (!view && !rehydrating) {
    return <Box sx={{ p: 3 }} data-testid="mr-chat-awaiting-handoff"><Typography>Continue in Home Chat to start or resume Commodities Market Research.</Typography></Box>;
  }
  return (
    <Paper square elevation={0} sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }} data-testid="mr-chat-panel">
      <Box ref={headingRef} tabIndex={-1} aria-label="Market Research conversation" sx={{ m: 0, outline: 'none' }}>
        <HomeChatWorkflowBanner
          title="Commodities Market Research"
          subtitle="Review market research and answer the next required questions."
          action={headerAction}
        />
      </Box>
      {writerRefreshRequired && <Alert severity="warning" data-testid="mr-chat-writer-refresh" sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }}>
        {writerRefreshFailed
          ? 'This saved conversation could not be refreshed. Another conversation may control this workflow. Your saved work is still available.'
          : 'Refresh this saved conversation before continuing.'}
        {onRehydrate && <Button onClick={refreshWriter} disabled={writerRefreshBusy}>
          Refresh saved conversation
        </Button>}
      </Alert>}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }}>{error}</Alert>}
      {pollRecovery === 'failed' && <Alert severity="warning" data-testid="mr-chat-progress-recovery" aria-live="polite" sx={{ mx: { xs: 2, sm: 3 }, mt: 1.5 }}>
        Market Research progress stopped. The latest saved work could not be loaded. Please refresh this chat.
      </Alert>}
      <Box ref={conversationRef} data-testid="mr-chat-conversation" aria-label="Market Research conversation and completed stages" aria-busy={isBusy} sx={{ overflowY: 'auto', flex: 1, minHeight: 0, px: { xs: 2, sm: 3 }, py: 1.5, pr: { xs: 2.5, sm: 3.5 } }}>
        <Stack spacing={1.25}>
          <WorkflowConversationAction />
          {mrrFlowId && !observerRunning && !observerIdle && <MarketResearchEarlyPlan flowId={mrrFlowId} viewOnly={observerReadOnly || view?.workflow?.read_only === true} disabled={isBusy} refreshKey={`${display(view?.current_stage?.key)}:${completed.length}:${view?.source_plan_state?.generation || ''}:${isBusy}`} />}
          {previousConversation && <Box data-testid="mr-chat-home-history">{previousConversation}</Box>}
          {entryAcknowledgement && (
            <Alert severity="info" icon={false} aria-live="polite" data-testid="mr-chat-entry-acknowledgement">
              {entryAcknowledgement}
            </Alert>
          )}
          {!previousConversation && durableInitiatingRequest && <Box data-testid="mr-chat-initiating-request" sx={{ alignSelf: 'flex-end', maxWidth: '86%', px: 1.5, py: 1, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', whiteSpace: 'pre-wrap' }}>
            {durableInitiatingRequest}
          </Box>}
          {conversationMessages.map((message, index) => (
            <Box key={`${message.role}-${index}`} data-testid={`mr-chat-message-${message.role}-${index}`} sx={{ alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '86%', px: 1.5, py: 1, borderRadius: 2, bgcolor: message.role === 'user' ? 'primary.main' : 'action.hover', color: message.role === 'user' ? 'primary.contrastText' : 'text.primary', whiteSpace: 'pre-wrap' }}>
              {display(message.content)}
            </Box>
          ))}
          {view?.current_stage?.key === 'product_discovery' && view?.recovery?.kind !== 'source_plan_review_required' && onOpenWorkflow && mrrFlowId && !observerReadOnly && (
            <Box data-testid="mr-chat-product-discovery-link">
              <Typography variant="body2" color="text.secondary">Choose sources and run product research in the workflow. Your saved conversation stays here.</Typography>
              <Button size="small" onClick={() => onOpenWorkflow(mrrFlowId, 'product_discovery')}>Open Product Discovery</Button>
            </Box>
          )}
          <Box aria-label="Completed Market Research stages" data-testid="mr-chat-completed-stages">
            <Stack spacing={0.75}>{completed.map((stage: MrChatCompletedStage) => {
              const stageKey = completedStageKey(stage);
              const expanded = expandedCompletedStages[stageKey] === true;
              const detail = display(stage.detail);
              const resultData = stageResultData(stage);
              return (
                <Accordion
                  key={stageKey}
                  expanded={expanded}
                  // This is a review timeline, not a comparison workspace:
                  // keep the active detail readable and avoid several large
                  // generated documents expanding at once.
                  onChange={(_, nextExpanded) => setExpandedCompletedStages(nextExpanded ? { [stageKey]: true } : {})}
                  disableGutters
                  elevation={0}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: '8px !important', bgcolor: 'action.hover', '&:before': { display: 'none' } }}
                  data-testid={`mr-chat-completed-stage-${stage.stage_key}`}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`mr-chat-completed-stage-details-${stage.stage_key}`}
                    id={`mr-chat-completed-stage-toggle-${stage.stage_key}`}
                    sx={{ px: 1.25, '& .MuiAccordionSummary-content': { my: 1.25, minWidth: 0 } }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{display(stage.label, stage.stage_key)}</Typography>
                      </Stack>
                    </Box>
                  </AccordionSummary>
                  {(expanded || (stage.stage_key === 'synopsis' && mrrOpened)) && <AccordionDetails id={`mr-chat-completed-stage-details-${stage.stage_key}`} sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
                    {stageKey === 'product_discovery' && productResearchActive
                      ? <Typography variant="caption" color="text.secondary">Product discovery is running. Updated results will appear when the search finishes.</Typography>
                      : <>
                    {detail && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', mt: 0.5 }}>{detail}</Typography>}
                    {resultData !== undefined && <Box sx={{ mt: detail ? 0.75 : 0 }} data-testid={`mr-chat-completed-stage-result-${stage.stage_key}`}><CompletedStageDetails stage={stage} data={resultData} onCopyQuestions={copyCompletedQuestions} copyFeedback={copyFeedback} /></Box>}
                    {!detail && resultData === undefined && !(stage.stage_key === 'synopsis' && canShowMrr) && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>No additional saved details are available for this stage.</Typography>}
                    {stage.stage_key === 'synopsis' && !isSynopsisForm && renderMrrAccordion()}
                    </>}
                  </AccordionDetails>}
                </Accordion>
              );
            })}</Stack>
          </Box>
          {isBusy && <Stack role="status" data-testid="mr-chat-processing" direction="row" spacing={1} alignItems="center" color="text.secondary"><CircularProgress size={18} aria-label={busyLabel} /><Typography variant="body2">{busyLabel}</Typography>{processingAction}</Stack>}
          {!writerRefreshRequired && !observerRunning && !isBusy && sectionCHandoffAction && !sectionCHandoffState && (
            <Alert severity="info" data-testid="mr-chat-section-c-handoff-required">
              <Typography variant="body2">Section C is complete. Hand it off to a CO/CS to continue Market Research.</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Button
                  variant="contained"
                  disabled={!onSectionCHandoff || sectionCHandoffBusy}
                  onClick={() => void submitSectionCHandoff()}
                >
                  {sectionCHandoffBusy ? 'Handing off to CO/CS…' : display(sectionCHandoffAction.label, 'Handoff to CO/CS')}
                </Button>
                {sectionCFlowId && (
                  <Button
                    variant="outlined"
                    onClick={() => onOpenWorkflow?.(sectionCFlowId, 'section_c')}
                  >
                    Open workflow
                  </Button>
                )}
              </Stack>
            </Alert>
          )}
          {!writerRefreshRequired && !observerRunning && !isBusy && sectionCHandoffState && (
            <Alert severity={view?.section_c_handoff?.no_counterpart === true ? 'warning' : 'success'} data-testid="mr-chat-section-c-handoff-progress">
              <Typography variant="body2">{sectionCHandoffMessage || 'Section C handoff status is available.'}</Typography>
              {!observerReadOnly && view?.section_c_handoff?.no_counterpart === true && sectionCFlowId && <FlightChiefHandoffHint flowId={sectionCFlowId} />}
              {sectionCFlowId && <Button variant="outlined" sx={{ mt: 1 }} onClick={() => onOpenWorkflow?.(sectionCFlowId, 'section_c')}>Open workflow</Button>}
            </Alert>
          )}
          {!writerRefreshRequired && !observerRunning && !isBusy && view?.recovery?.kind === 'product_discovery_empty' && (
            <Alert severity="info" data-testid="mr-chat-product-discovery-empty">
              <Typography variant="body2">{display(view.recovery.message, 'No products were found for the approved requirements. Downstream research is paused.')}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {view.recovery.open_workflow && action && <Button variant="contained" onClick={() => onOpenWorkflow?.(action.flowId, action.stage)}>Open workflow</Button>}
                {view.recovery.start_over && <Button variant="outlined" onClick={() => void submit('start_over')}>Start over</Button>}
              </Stack>
            </Alert>
          )}
          {!writerRefreshRequired && !observerRunning && !isBusy && view?.recovery?.kind === 'source_plan_review_required' && (
            <Alert severity="warning" data-testid="mr-chat-source-plan-review">
              <Typography variant="body2">{display(view.recovery.message, 'Source determination needs review before Market Research can continue.')}</Typography>
              {view.recovery.open_workflow && action && (
                <Button variant="contained" sx={{ mt: 1 }} onClick={() => onOpenWorkflow?.(action.flowId, action.stage)}>
                  Open Product Discovery
                </Button>
              )}
            </Alert>
          )}
          {!observerReadOnly && !isBusy && view?.recovery?.kind === 'naics_classification_required' && (
            <Alert severity="warning" data-testid="mr-chat-naics-classification-required">
              <Typography variant="body2">{display(view.recovery.message, 'No current products have a resolved NAICS code. Open NAICS Mapping to classify them, then resume this chat.')}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {view.recovery.open_workflow && action && <Button variant="contained" onClick={() => onOpenWorkflow?.(action.flowId, action.stage)}>Open workflow</Button>}
                {view.recovery.can_resume === true && <Button variant="outlined" onClick={() => void submit('resume')}>Resume</Button>}
              </Stack>
            </Alert>
          )}
          {!writerRefreshRequired && !observerRunning && !isBusy && interrupted && (
            <Alert severity="warning" aria-live="polite" data-testid="mr-chat-execution-interrupted">
              <Typography variant="body2">This workflow was interrupted.</Typography>
              {view?.recovery?.can_resume === true && (
                <Button variant="contained" sx={{ mt: 1 }} onClick={() => void submit('resume')}>Resume</Button>
              )}
            </Alert>
          )}
          {!observerReadOnly && !isBusy && view?.recovery?.kind === 'coordination_checkpoint' && view.recovery.can_resume === true && (
            <Alert severity="info" data-testid="mr-chat-coordination-checkpoint">
              <Typography variant="body2">{display(view.recovery.message, 'Complete the applicable human coordination in the workflow, then resume here.')}</Typography>
              <Button variant="contained" sx={{ mt: 1 }} onClick={() => void submit('resume')}>Resume</Button>
            </Alert>
          )}
          {!writerRefreshRequired && !observerRunning && !isBusy && action && view?.recovery?.kind !== 'product_discovery_empty' && view?.recovery?.kind !== 'source_plan_review_required' && view?.recovery?.kind !== 'naics_classification_required' && (
    <Box>
      <Button variant="contained" onClick={() => onOpenWorkflow?.(action.flowId, action.stage)}>
        {action.terminal ? 'Review and Verify Workflow' : 'Open workflow'}
      </Button>
    </Box>
          )}
          {!isBusy && waitingForExecution && !action && (
            <Alert severity="info" aria-live="polite" data-testid="mr-chat-execution-wait">
              <Typography variant="body2">
                This Market Research workflow is running.
              </Typography>
            </Alert>
          )}
          {observerRunning && (
            <Alert severity="info" icon={false} aria-live="polite" data-testid="mr-chat-live-observer">
              Viewing live progress. This Market Research workflow is running.
            </Alert>
          )}
          {!isBusy && pending && !observerReadOnly && !waitingForExecution && !action && view?.recovery?.kind !== 'product_discovery_empty' && (
            <Box ref={formRef} component={isSynopsisForm ? 'div' : 'form'} noValidate={!isSynopsisForm} onSubmit={isSynopsisForm ? undefined : (event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); void submit(); }} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <ChatBubbleDisclosure key={JSON.stringify([view?.session_id, display(pending.stage_key), display(pending.kind), (pending.fields || []).map(field => [fieldKey(field), fieldInputType(field)])])} label="Market Research form" preview="" alwaysCollapsible defaultExpanded title={!isGenericStageFormCopy(display(pending.prompt)) ? <Typography sx={{ fontWeight: 600 }}>{display(pending.prompt)}</Typography> : undefined}>
              {display(pending.instructions) && !isGenericStageFormCopy(display(pending.instructions)) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{display(pending.instructions)}</Typography>}
              {(pending.validation_errors || []).some((item) => display(item.message) && !isInlineValidationError(item, interactiveFields, values)) && (
                <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="mr-chat-validation-errors">
                  <Stack spacing={0.5}>
                    {Array.from(new Set((pending.validation_errors || [])
                      .filter((item) => !isInlineValidationError(item, interactiveFields, values))
                      .map((item) => display(item.message)).filter(Boolean)))
                      .map((message) => <Typography key={message} variant="body2">{message}</Typography>)}
                  </Stack>
                </Alert>
              )}
              {/* Prior-stage results are controller context only. Showing their
                  nested payloads here overwhelms active C/D/E questions; the
                  canonical generated text remains available after completion
                  in its read-only completed-stage card. */}
              {canNotifyCustomersForQuestions && <Box sx={{ mt: 1.5 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <MarketResearchNotifyControls
                    flowId={view.workflow?.flow_id as string}
                    context="questions"
                    isOwner={canManageQuestionsShare}
                    onOpenShare={() => setQuestionsShareOpen(true)}
                    onRequestNotification={onNotifyQuestions}
                  />
                </Stack>
              </Box>}
              <Box component={isSynopsisForm ? 'form' : 'div'} id={isSynopsisForm ? pendingFormId : undefined} noValidate={isSynopsisForm} onSubmit={isSynopsisForm ? (event: React.FormEvent<HTMLFormElement>) => { event.preventDefault(); void submit(); } : undefined}>
              <Stack spacing={1.25} sx={{ mt: 1.5 }}>{interactiveFields.map((field) => {
                const key = fieldKey(field);
                const label = fieldLabel(field);
                const required = fieldIsRequired(field);
                const value = values[key];
                const inputType = fieldInputType(field);
                const inputId = `mr-chat-field-${key}`;
                const visibleLabel = isSynopsisForm ? `${label} (${required ? 'Required' : 'Optional'})` : label;
                const duplicatesContextPrompt = isContextQuestions && label.trim() === display(pending.prompt).trim();
                const control = inputType === 'boolean'
                  ? <FormControlLabel control={<Checkbox checked={value === true} onChange={(event) => updateValues((current) => ({ ...current, [key]: event.target.checked }))} />} label={visibleLabel} />
                  : inputType === 'file' || inputType === 'files'
                    ? <Button component="label" variant="outlined">{visibleLabel}<input hidden type="file" accept="application/pdf" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} /></Button>
                    : inputType === 'line_items'
                      ? <MrChatLineItemsField
                        field={field}
                        label={label}
                        required={required}
                        value={value}
                        onChange={(next) => updateValues((current) => ({ ...current, [key]: next }))}
                      />
                    : inputType === 'radio' || inputType === 'form_type_choice'
                      ? <FormControl required={required} error={required && isMissingAnswer(value)} fullWidth><FormLabel>{visibleLabel}</FormLabel><RadioGroup aria-label={visibleLabel} value={typeof value === 'string' ? value : ''} onChange={(event) => isSynopsisForm && isSynopsisFormSelector(field) ? selectSynopsisForm(event.target.value) : updateValues((current) => ({ ...current, [key]: event.target.value }))}>{options(field).map((option) => <FormControlLabel key={option.value} value={option.value} control={<Radio />} label={option.label} />)}</RadioGroup></FormControl>
                      : inputType === 'checkboxes' || inputType === 'multi_select'
                        ? <FormControl required={required} error={required && isMissingAnswer(value)} fullWidth><FormLabel>{visibleLabel}</FormLabel>{options(field).map((option) => {
                          const selected = isStringArray(value) && value.includes(option.value);
                          return <FormControlLabel key={option.value} control={<Checkbox checked={selected} onChange={(event) => updateValues((current) => {
                            const currentValue = current[key];
                            const currentValues: string[] = isStringArray(currentValue) ? [...currentValue] : [];
                            return { ...current, [key]: event.target.checked ? [...currentValues, option.value] : currentValues.filter((item) => item !== option.value) };
                          })} />} label={option.label} />;
                        })}</FormControl>
                        : inputType === 'select'
                          ? isSynopsisForm && (key === 'instrument_type' || key === 'form_type')
                            ? <Box sx={{ width: '100%' }}>
                              <FormLabel component="label" htmlFor={inputId} sx={{ display: 'block', mb: 0.5 }}>{visibleLabel}</FormLabel>
                              <TextField
                                id={inputId}
                                data-testid="mr-chat-synopsis-form-type"
                                select
                                SelectProps={{ native: true }}
                                fullWidth
                                value={value ?? ''}
                                required={required}
                                error={required && isMissingAnswer(value)}
                                onChange={(event) => selectSynopsisForm(event.target.value)}
                              >
                                {options(field).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </TextField>
                              {options(field).find((option) => option.value === value)?.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {options(field).find((option) => option.value === value)?.description}
                              </Typography>}
                            </Box>
                            : <Box><FormLabel component="label" htmlFor={inputId} required={required && !isSynopsisForm} sx={{ display: 'block', mb: 0.5 }}>{visibleLabel}</FormLabel><TextField id={inputId} select fullWidth value={value ?? ''} required={required} error={required && isMissingAnswer(value)} SelectProps={{ inputProps: { 'aria-label': visibleLabel } }} onChange={(event) => updateValues((current) => ({ ...current, [key]: event.target.value }))}>{options(field).map((option) => <MenuItem key={option.value} value={option.value}><Stack spacing={0.25}><Typography variant="body2">{option.label}</Typography>{option.description && <Typography variant="caption" color="text.secondary">{option.description}</Typography>}</Stack></MenuItem>)}</TextField></Box>
                        : <Box>
                          {!isContextQuestions && <FormLabel component="label" htmlFor={inputId} required={required && !isSynopsisForm} sx={{ display: 'block', mb: 0.5 }}>{visibleLabel}</FormLabel>}
                          <TextField
                          id={inputId}
                          data-testid={isContextQuestions ? `mr-chat-context-answer-${key}` : undefined}
                          value={value ?? ''}
                          required={required}
                          error={required && isMissingAnswer(value)}
                          fullWidth={isContextQuestions || isSynopsisForm}
                          multiline={inputType === 'textarea' || isContextQuestions || isSynopsisForm}
                          minRows={inputType === 'textarea' || isContextQuestions || isSynopsisForm ? 3 : undefined}
                          type={inputType === 'number' ? 'number' : inputType === 'date' ? 'date' : inputType === 'email' ? 'email' : inputType === 'url' ? 'url' : 'text'}
                          inputProps={isContextQuestions ? { 'aria-label': `Answer: ${label}` } : undefined}
                          onChange={(event) => updateValues((current) => ({ ...current, [key]: event.target.value }))}
                          helperText={display(field.help_text) || display(field.description)}
                        /></Box>;
                return isContextQuestions ? (
                  <Paper key={key} variant="outlined" sx={{ p: 1.5, width: '100%' }} data-testid={`mr-chat-context-question-${key}`}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Chip size="small" label={required ? 'Required' : 'Optional'} color={required ? 'error' : 'success'} />
                      {!duplicatesContextPrompt && <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>}
                    </Stack>
                    {control}
                  </Paper>
                ) : <Box key={key}>{control}</Box>;
              })}</Stack>
              </Box>
              {files.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{files.map((file) => file.name).join(', ')}</Typography>}
              {isSynopsisForm && renderMrrAccordion()}
              <Button type="submit" form={isSynopsisForm ? pendingFormId : undefined} variant="contained" sx={{ mt: 2 }}>Save and continue</Button>
              </ChatBubbleDisclosure>
            </Box>
          )}
          <Box data-testid="mr-chat-scroll-anchor" />
        </Stack>
      </Box>
      {canManageQuestionsShare && (
        <MarketResearchNotifyShareDialog
          flowId={typeof view?.workflow?.flow_id === 'string'
            ? view.workflow.flow_id : null}
          context="questions"
          open={questionsShareOpen}
          onClose={() => setQuestionsShareOpen(false)}
          onShareAndNotify={onShareAndNotifyQuestions}
        />
      )}
    </Paper>
  );
};

export default MrChatPanel;
